import { Accordion, AccordionItem } from "@heroui/accordion";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { GetLocalStorage } from "../../../../../utils/functions/GetLocalStorage.js";
import { FormatDateTime } from "../../../../../utils/functions/ResourceFunctions.js";
import {
  useCalculateExpectedScoreMutation,
  useCalculateKraScoreMutation,
  useCalculateMainScoreMutation,
  useGenerateReportQuery,
  useGetMainSelfReviewByAppraisalTypeQuery,
  useSaveSelfKraMutation,
  useSubmitSlefReviewMutation,
} from "../../../../api/employeeApi.js";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import { ActionButtons } from "../../../../global/components/Buttons/buttons";
import {
  FormInput,
  FormSelect,
  FormTextArea,
} from "../../../Components/Forms/InputTemplate";
import PageStyle from "../../../Components/Layout/PageLayout";
import bull from "../../../assets/bull.png";
import flag from "../../../assets/finish-FLAG.png";
import person from "../../../assets/personAppraisal.png";
import { MeasurementTypeFormatter } from "../../HR/Appraisal/Modals/data.js";
import { LineManager } from "./LineManager.jsx";
import { Content1, MetaData } from "./data.jsx";
const SelfAppraialView = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = React.useState("self");
  const user = GetLocalStorage();
  // const { data } = useGetMainSelfReviewQuery(id);
  const [format, setFormat] = useState(1);
  const { data } = useGetMainSelfReviewByAppraisalTypeQuery({
    referenceId: id,
    appraisalType: 0,
    appraiseeId: user.staffId,
  });
  const { data: report } = useGenerateReportQuery({
    employeeId: user.staffId,
    referenceId: id,
    format: format,
  });
  const handleDownloadReport = () => {
    if (!report) {
      toast.error("Report not available for download.");
      return;
    }

    // If the API returns an error wrapper, show message and stop.
    if (report?.successful === false) {
      toast.error(report?.statusMessage || "Report not available for download.");
      return;
    }

    const fmt = parseInt(format, 10) || 1;
    const excelMime =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const pdfMime = "application/pdf";

    const base64ToBlob = (base64, mime) => {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: mime });
    };

    let fileBlob = null;

    // Handle different possible response shapes
    try {
      if (report instanceof Blob) {
        fileBlob = report;
      } else if (report instanceof ArrayBuffer) {
        fileBlob = new Blob([report], {
          type: fmt === 1 ? excelMime : pdfMime,
        });
      } else if (typeof report === "object" && report.data) {
        const payload = report.data;
        if (payload instanceof Blob) {
          fileBlob = payload;
        } else if (payload instanceof ArrayBuffer) {
          fileBlob = new Blob([payload], {
            type: fmt === 1 ? excelMime : pdfMime,
          });
        } else if (typeof payload === "string") {
          // assume base64 string
          fileBlob = base64ToBlob(payload, fmt === 1 ? excelMime : pdfMime);
        }
      } else if (typeof report === "string") {
        // assume base64 string
        fileBlob = base64ToBlob(report, fmt === 1 ? excelMime : pdfMime);
      }
    } catch (err) {
      console.error("Error preparing report for download:", err);
      toast.error("Could not process report for download.");
      return;
    }

    if (!fileBlob) {
      toast.error("Report not available for download.");
      return;
    }

    const blobUrl = window.URL.createObjectURL(fileBlob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download =
      fmt === 1
        ? `Appraisal_Report_${user.fullName}.xlsx`
        : `Appraisal_Report_${user.fullName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const [kraId, setKraId] = useState(0);
  // Memoize allObjectives
  const allObjectives = useMemo(() => {
    return data?.responseObject?.objectives || [];
  }, [data?.responseObject?.objectives]);
  const [onSubmit] = useSubmitSlefReviewMutation();
  const formMethods = useForm({
    mode: "all",
    defaultValues: {
      MostDifficultElement: "",
      PerformanceOutcomeComment: "",
    },
  });
  const employeeData = data?.responseObject?.employee;

  const { handleSubmit: formHandleSubmit, watch } = formMethods;
  const [onCalculate] = useCalculateKraScoreMutation();
  const [expectedScore] = useCalculateExpectedScoreMutation();
  const watched = useWatch({ control: formMethods.control });
  const initialized = React.useRef(false);
  const debounceRef = React.useRef(null);
  const debounceRefMain = React.useRef(null);
  const debounceExpected = React.useRef(null);
  const [onCalculateMainScore] = useCalculateMainScoreMutation();
  const [score, setScore] = useState({
    actualValue: 0,
    targetValue: 0,
    weight: 0,
  });
  const [kraPerformanceScores, setKraPerformanceScores] = useState({});
  const [totalScore, setTotalScore] = useState({
    colorType: "",
    totalAppraisalScore: 0,
    performanceStatusLabelName: "",
  });
  const [maxScore, setMaxScore] = useState(0);
  const [item, setItem] = useState();
  const [KraIndex, setKraIndex] = useState();
  const [objIndex, setObjIndex] = useState();
  const currentActualValue =
    objIndex !== undefined && KraIndex !== undefined
      ? watch(`KRAs_${objIndex}_${KraIndex}_actualScore`)
      : 0;
  useEffect(() => {
    if (!score.actualValue && !score.targetValue && !score.weight) return;
    if (!item?.kraId) return;

    if (debounceRefMain.current) clearTimeout(debounceRefMain.current);
    debounceRefMain.current = setTimeout(async () => {
      try {
        const res = await onCalculateMainScore({
          actualValue: score.actualValue,
          targetValue: score.targetValue,
          weight: score.weight,
        }).unwrap();

        if (res) {
          setKraPerformanceScores((prev) => ({
            ...prev,
            [item.kraId]: res.kraPerformanceScore,
          }));
        }
      } catch (err) {
        console.error("Error calculating main score:", err);
      }
    }, 500);

    return () => {
      if (debounceRefMain.current) clearTimeout(debounceRefMain.current);
    };
  }, [score, onCalculateMainScore, item?.kraId]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    if (!watched || !allObjectives.length) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const objectives = allObjectives.map((objective, objIndex) => {
          const krAs = objective.krAs.map((kra, kraIndex) => {
            const kraKey = `${objIndex}_${kraIndex}`;
            const actualScore =
              watched[`KRAs_${kraKey}_actualScore`] ||
              kra?.employeeReviewDetails?.find(
                (item) => item.appraisalType === 0
              )?.kraActualScore ||
              0;
            return {
              kraId: kra.kraId,
              actualValue: parseInt(actualScore) || 0,
              targetValue: kra.targetValue || 0.01,
              weight: kra.weight,
            };
          });
          return {
            objectiveId: objective.objectiveId,
            objectiveWeight: objective.objectiveWeight,
            krAs,
          };
        });

        const res = await onCalculate({
          employeeId: employeeData?.id || 0,
          referenceId: id,
          objectives,
        }).unwrap();
        if (res) {
          setTotalScore({
            colorType: res.colorType,
            totalAppraisalScore: res.totalAppraisalScore,
            performanceStatusLabelName: res.performanceStatusLabelName,
          });
        }
      } catch (err) {
        console.error("Error calculating total actual score:", err);
      }
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [watched, employeeData?.id, id, onCalculate, formMethods]);

  useEffect(() => {
    if (item?.kraId && objIndex !== undefined && KraIndex !== undefined) {
      setScore({
        actualValue: parseFloat(currentActualValue) || 0,
        targetValue: item.targetValue,
        weight: item.weight,
      });
    }
  }, [item, KraIndex, objIndex, currentActualValue]);

  const handleFormSubmit = async (formData) => {
    try {
      const formattedData = {
        referenceId: id || "0",
        appraiseeId: employeeData?.id,
        mostDifficultElement: formData.MostDifficultElement || "",
        performanceOutcomeComment: formData.PerformanceOutcomeComment || "",
      };

      const response = await onSubmit(formattedData).unwrap();
      if (response.successful) {
        toast.success(response?.statusMessage);
      }
    } catch (err) {
      toast.error(err.data?.statusMessage);
    }
  };

  const [onSave, { isLoading }] = useSaveSelfKraMutation();
  const handleSave = async () => {
    try {
      let foundObjIndex = -1;
      let foundKraIndex = -1;
      allObjectives?.forEach((objective, objIdx) => {
        objective.krAs?.forEach((kra, kraIdx) => {
          if (kra.kraId === kraId) {
            foundObjIndex = objIdx;
            foundKraIndex = kraIdx;
            // kraScore = kra.kraScore || 0;
          }
        });
      });

      if (foundObjIndex === -1 || foundKraIndex === -1) {
        toast.error("KRA not found");
        return;
      }

      const kraKey = `${foundObjIndex}_${foundKraIndex}`;
      const kraActualScore =
        formMethods.getValues(`KRAs_${kraKey}_actualScore`) || 0;
      const comment = formMethods.getValues(`KRAs_${kraKey}_comments`) || "";
      const artifacts = formMethods.getValues(`KRAs_${kraKey}_artifacts`);

      const formData = new FormData();
      formData.append("ReferenceId", id);
      formData.append("KRAId", kraId);
      formData.append("AppraiseeId", employeeData?.id);
      formData.append("KRAActualScore", parseFloat(kraActualScore));
      formData.append("KRAScore", parseFloat(kraPerformanceScores[kraId] ?? 0));
      formData.append("Comment", comment);
      if (artifacts && artifacts.length > 0) {
        artifacts.forEach((artifact) => {
          formData.append(`Artifacts`, artifact.file);
        });
      }

      const res = await onSave(formData).unwrap();
      if (res.successful) {
        toast.success(res?.statusMessage || "KRA saved successfully");
      }
    } catch (err) {
      toast.error("Save failed: " + (err.data?.statusMessage || err.message));
    }
  };
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    if (!allObjectives.length) return;
    if (debounceExpected.current) {
      clearTimeout(debounceExpected.current);
    }
    debounceExpected.current = setTimeout(async () => {
      try {
        const objectives = allObjectives.map((objective) => {
          const krAs = objective.krAs.map((kra) => {
            return {
              kraId: kra.kraId,
              actualValue: kra.targetValue || 0,
              targetValue: kra.targetValue || 0.01,
              weight: kra.weight,
            };
          });
          return {
            objectiveId: objective.objectiveId,
            objectiveWeight: objective.objectiveWeight,
            krAs,
          };
        });

        const res = await expectedScore({
          employeeId: employeeData?.id || 0,
          referenceId: id,
          objectives,
        }).unwrap();
        if (res) {
          setMaxScore(res.totalAppraisalScore);
        }
      } catch (err) {
        console.error("Error calculating total actual score:", err);
      }
    }, 500);

    return () => {
      if (debounceExpected.current) {
        clearTimeout(debounceExpected.current);
      }
    };
  }, [employeeData?.id, id, expectedScore, formMethods, allObjectives]);
  const colors = ["red", "yellow", "blue", "black", "white", "green"];
  const formats = [
    { value: 1, label: "Excel" },
    { value: 2, label: "PDF" },
  ];
  return (
    <PageStyle hasBack={true} backURL={"/dashboard/employee/Self-Appraisal"}>
      <FormProvider {...formMethods}>
        <div>
          <h1 className="bg-gradient-to-r from-[#1F311D] to-[#5E975A] text-transparent bg-clip-text font-[600] text-3xl mb-6">
            {activeTab === "self"
              ? "APPRAISEE SELF-ASSESSMENT"
              : "APPRAISEE ASSESSMENT"}
          </h1>

          <div className="p-4 bg-white rounded-lg shadow-md">
            <div className="mb-4">
              <div
                style={{ display: "flex", borderBottom: "1px solid #305F32" }}
              >
                <button
                  type="button"
                  onClick={() => setActiveTab("self")}
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    borderBottom:
                      activeTab === "self" ? "2px solid #305F32" : "none",
                    background: "none",
                    cursor: "pointer",
                    fontWeight: activeTab === "self" ? "bold" : "normal",
                  }}
                >
                  Self Assessment
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("manager")}
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    borderBottom:
                      activeTab === "manager" ? "2px solid #305F32" : "none",
                    background: "none",
                    cursor: "pointer",
                    fontWeight: activeTab === "manager" ? "bold" : "normal",
                  }}
                >
                  Line Manager Assessment
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("HR")}
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    borderBottom:
                      activeTab === "HR" ? "2px solid #305F32" : "none",
                    background: "none",
                    cursor: "pointer",
                    fontWeight: activeTab === "HR" ? "bold" : "normal",
                  }}
                >
                  HR Assessment
                </button>
              </div>
            </div>
            <div style={{ padding: "16px 0" }}>
              {activeTab === "self" ? (
                <div>
                  <form
                    id="selfAssessmentForm"
                    onSubmit={formHandleSubmit(handleFormSubmit)}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="space-y-2">
                        <FormSelect
                          title={"Select File Formats"}
                          camelCase={"Format"}
                          placeholder={"select"}
                          array={formats.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                          onChange={(e) => setFormat(e.target.value)}
                        />
                        <ActionButtons
                          width={"auto"}
                          onClick={handleDownloadReport}
                          className={DashboardStyle?.button_cage_weight}
                          bg="var(--primary-color)"
                          type="button"
                        >
                          Download Appraisal Report
                        </ActionButtons>
                      </div>

                      <div className="flex items-center space-x-3">
                        <div>
                          <p className="text-[#00AB09]">
                            Total Score: {totalScore.totalAppraisalScore}/
                            {maxScore} (
                            {totalScore.totalAppraisalScore / maxScore})%
                          </p>
                        </div>

                        {totalScore.performanceStatusLabelName && (
                          <>
                            <div>|</div>
                            <div>
                              <p className="text-[#00AB09] text-center">
                                Status:{" "}
                                <span
                                  style={{
                                    color: `${colors[totalScore?.colorType ?? 0]
                                      }`,
                                    backgroundColor: `${colors[totalScore?.colorType ?? 0] ===
                                      "white" && "#305F32"
                                      } `,
                                  }}
                                  className="px-2 py-1 text-center"
                                >
                                  {totalScore.performanceStatusLabelName}{" "}
                                </span>
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <Accordion
                      aria-label={`Accordion 1`}
                      selectionMode="single"
                      defaultExpandedKeys={["1"]}
                      titleWrapper="shadow-md rounded-md border border-gray-200"
                    >
                      <AccordionItem
                        title={
                          <div className="flex items-center gap-5 px-5">
                            <img src={person} alt="" />{" "}
                            <span className="text-[#305F32] font-[600]">
                              EMPLOYEE PROFILE SUMMARY
                            </span>
                          </div>
                        }
                        className="shadow-md rounded-md border border-gray-200 py-3 mb-2"
                      >
                        <MetaData data={employeeData} />
                      </AccordionItem>
                    </Accordion>
                    {allObjectives?.map((item, index) => (
                      <Accordion
                        aria-label={`Accordion ${index + 1}`}
                        key={index + 1}
                        selectionMode="single"
                        titleWrapper="shadow-md rounded-md border border-gray-200"
                      >
                        <AccordionItem
                          key={index + 1}
                          title={
                            <div className="flex items-center justify-between px-5">
                              <div className="flex items-center gap-5">
                                <img src={flag} alt="" />
                                <span className="text-[#4B664C] font-[600]">
                                  OBJECTIVE: {item?.objectiveTitle}
                                </span>
                              </div>
                              <div>
                                <span className="text-[#4B664C] font-[600]">
                                  Weight: {item?.objectiveWeight}%
                                </span>
                              </div>
                              <div className="w-10" />
                            </div>
                          }
                          className="shadow-md rounded-md border border-gray-200 py-3 mb-2"
                        >
                          <div>
                            {item.krAs.map((items, kraIndex) => (
                              <Accordion
                                key={kraIndex}
                                aria-label={`Accordion ${kraIndex + 1}`}
                                selectionMode="single"
                                defaultExpandedKeys={["1"]}
                                titleWrapper="shadow-md rounded-md border border-gray-200"
                                onClick={() => {
                                  setKraId(items.kraId);
                                  setItem(items);
                                  setKraIndex(kraIndex);
                                  setObjIndex(index);
                                }}
                              >
                                <AccordionItem
                                  key={kraIndex}
                                  title={
                                    <div className="flex items-center gap-5 px-5">
                                      <img src={bull} alt="" />
                                      <span className="text-[#4B664C] font-[600]">
                                        KRA: {items?.kraTitle}
                                      </span>
                                    </div>
                                  }
                                  className="shadow-md rounded-md border border-gray-200 py-3 mb-2"
                                >
                                  <div>
                                    <div className="flex items-center justify-between px-5">
                                      <div />
                                      <div>
                                        <p className="text-[#4B664C] font-[600]">
                                          isAutogenerated:{" "}
                                          {items.isAutoGenerated ? "Yes" : "No"}
                                        </p>
                                      </div>
                                    </div>

                                    <div
                                      key={kraIndex + 1}
                                      className="flex items-center gap-5 py-2 px-5"
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "180px 1fr",
                                        alignItems: "center",
                                        gap: "16px",
                                      }}
                                    >
                                      <h2 className="text-[#305F32] font-[600]">
                                        Title: {items.kraTitle}
                                      </h2>
                                      <h4 className="text-[#305F32] font-[500]">
                                        Description: {items.kraDescription}
                                      </h4>
                                      <h2 className="text-[#305F32] font-[600]">
                                        Target: {items.targetValue}
                                      </h2>
                                      <h2 className="text-[#305F32] font-[600]">
                                        Weight: {items.weight}
                                      </h2>
                                      <h2 className="text-[#305F32]+font-[600]">
                                        Threshold: {items.threshold}
                                      </h2>
                                      <h4 className="text-[#00AB09] font-[500]">
                                        Score:{" "}
                                        {kraPerformanceScores[items.kraId] ||
                                          items?.employeeReviewDetails?.filter(
                                            (item) => item.appraisalType === 0
                                          )?.[0]?.kraScore}
                                      </h4>
                                      <h4 className="text-[#305F32] font-[500]">
                                        Measurement Type:{" "}
                                        {MeasurementTypeFormatter(
                                          items.measurementType
                                        )}
                                      </h4>
                                      {!items.isAutoGenerated && (
                                        <h4 className="text-[#305F32] font-[500]">
                                          Source: {items.source}
                                        </h4>
                                      )}
                                      <h4 className="text-[#305F32] font-[500]">
                                        KRA Type:{" "}
                                        {items.kraType === 1
                                          ? "Core"
                                          : "None Core"}
                                      </h4>
                                      <h4 className="text-[#305F32] font-[500]">
                                        Include Self Review:{" "}
                                        {items.includeSelfReview ? "Yes" : "No"}
                                      </h4>
                                      <h4 className="text-[#305F32] font-[500]">
                                        Period Start Date:{" "}
                                        {FormatDateTime(
                                          items.periodStartDate,
                                          "DD/MM/YYYY"
                                        )}
                                      </h4>
                                      <h4 className="text-[#305F32] font-[500]">
                                        Period End Date:{" "}
                                        {FormatDateTime(
                                          items.periodEndDate,
                                          "DD/MM/YYYY"
                                        )}
                                      </h4>

                                      <h4 className="text-[#305F32] font-[500]">
                                        Next Due Date:{" "}
                                        {FormatDateTime(
                                          items.nextDueDate,
                                          "DD/MM/YYYY"
                                        )}
                                      </h4>
                                      <h4 className="text-[#305F32] font-[500]">
                                        SLA Due Date:{" "}
                                        {FormatDateTime(
                                          items.slaDueDate,
                                          "DD/MM/YYYY"
                                        )}
                                      </h4>
                                      <h4 className="text-[#305F32] font-[500]">
                                        Appraisal Period:{" "}
                                        {items.appraisalPeriod}
                                      </h4>
                                      <h4 className="text-[#305F32] font-[500]">
                                        SLA Reminder Count:{" "}
                                        {items.slaReminderCount}
                                      </h4>
                                      <h4 className="text-[#305F32] font-[500]">
                                        SLA Start Duration:{" "}
                                        {items.slaStartDuration}
                                      </h4>
                                    </div>
                                  </div>

                                  <div>
                                    <Content1
                                      id={items.kraId}
                                      objIndex={index}
                                      kraIndex={kraIndex}
                                      defaultValues={
                                        items?.employeeReviewDetails?.filter(
                                          (item) => item.appraisalType === 0
                                        )?.[0]
                                      }
                                      formMethods={formMethods}
                                      items={items}
                                      ratingScales={items?.ratingScales}
                                    />
                                    <div
                                      style={{ marginTop: "1rem" }}
                                      className={`flex justify-end mr-5`}
                                    >
                                      <ActionButtons
                                        width={"auto"}
                                        type={"button"}
                                        onClick={handleSave}
                                        className={
                                          DashboardStyle?.button_cage_weight
                                        }
                                        bg="var(--primary-color)"
                                      >
                                        {isLoading ? "Saving..." : "Save"}
                                      </ActionButtons>
                                    </div>
                                  </div>
                                </AccordionItem>
                              </Accordion>
                            ))}
                          </div>
                        </AccordionItem>
                      </Accordion>
                    ))}
                    {data?.responseObject?.hodComment && (
                      <div className="mt-8 p-4">
                        <h3 className="text-lg font-semibold text-[#305F32] mb-4">
                          H.O.D Comment
                        </h3>
                        <p className="text-[#305f32] text-sm font-normal">
                          {data?.responseObject?.hodComment} testing
                        </p>
                      </div>)}

                    {data?.responseObject?.hodRecommendation && <div className="mt-0 p-4">
                      <h3 className="text-lg font-semibold text-[#305F32] mb-4">
                        H.O.D Recommendation
                      </h3>
                      <p className="text-[#305f32] text-sm font-normal">
                        {data?.responseObject?.hodRecommendation} testing
                      </p>
                    </div>}
                    <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-lg font-semibold text-[#305F32] mb-4">
                        General Assessment
                      </h3>
                      <div className="space-y-4">
                        <FormInput
                          title={"Most Difficult Element"}
                          camelCase={"MostDifficultElement"}
                          placeholder={"Enter the most difficult element"}
                          className=""
                          type="text"
                          isRequired={true}
                        />
                        <FormTextArea
                          className="h-[150px]"
                          title={"Performance Outcome Comment"}
                          camelCase={"PerformanceOutcomeComment"}
                          placeholder="Enter your overall performance comments"
                          type="text"
                          textAreaClassName=""
                          isRequired={true}
                        />
                      </div>
                    </div>
                    <div
                      style={{ marginTop: "1rem" }}
                      className={`flex justify-end mr-5`}
                    >
                      <ActionButtons
                        width={"auto"}
                        type={"submit"}
                        className={DashboardStyle?.button_cage_weight}
                        bg="var(--primary-color)"
                      >
                        Submit
                      </ActionButtons>
                    </div>
                  </form>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div></div>
                    <div>
                      <p className="text-[#00AB09]">
                        {/* Total Actual Score: {totalActualScore} */}
                      </p>
                    </div>
                  </div>
                  <div className="mb-4"></div>
                  <Accordion
                    aria-label={`Accordion 1`}
                    selectionMode="single"
                    defaultExpandedKeys={["1"]}
                    titleWrapper="shadow-md rounded-md border border-gray-200"
                  >
                    <AccordionItem
                      title={
                        <div className="flex items-center gap-5 px-5">
                          <img src={person} alt="" />{" "}
                          <span className="text-[#305F32] font-[600]">
                            EMPLOYEE PROFILE SUMMARY
                          </span>
                        </div>
                      }
                      className="shadow-md rounded-md border border-gray-200 py-3 mb-2"
                    >
                      <MetaData data={employeeData} />
                    </AccordionItem>
                  </Accordion>
                  <LineManager activeTab={activeTab} />
                </div>
              )}
            </div>
          </div>
        </div>
      </FormProvider>
    </PageStyle>
  );
};

export default SelfAppraialView;
